import { GetDebitor_debitor, GetInvoices_invoices } from './GraphQL';
import { EditableInvoiceLine } from './Pages/SingleCase/Invoice/InvoiceModal';

export interface SumFeeResult {
	sum: number;
	fee: number;
}

export const DEFAULT_ENVFEE_NAME = 'ENVFEE';
export const DEFAULT_SUPPLYFEE_NAME = 'SUPPLYFEE';
export const UNDERLEV_VIDEREFAKT_LINE_CODE = 'UNDERLEV_VIDEREFAKT';
export const SUBCONTRACTOR_LINE_CODE = 'SUBCONTRACTOR';
export const SUBCONTRACTOR_FEE_LINE_CODE = 'SUBCONTRACTOR_FEE';
export const INSURANCE_FEE_LINE_CODE = 'INSURANCE_FEE';

const SUPPLY_FEE_APPLICABLE_CATEGORIES = new Set<string>(['FINAL', 'SUB', 'SCREENING']);
const SUPPLY_FEE_IGNORED_STATUSES = new Set<string>(['REJECTED']);

const SUPPLY_FEE_EXCLUDED_LINE_CODES = new Set<string>([
	DEFAULT_ENVFEE_NAME,
	DEFAULT_SUPPLYFEE_NAME,
	UNDERLEV_VIDEREFAKT_LINE_CODE,
	SUBCONTRACTOR_LINE_CODE,
	SUBCONTRACTOR_FEE_LINE_CODE,
	INSURANCE_FEE_LINE_CODE,
]);

const EMPTY_SUPPLY_FEE_DATE = '0001-01-01';

export type SupplyFeeDebitor = {
	supplyFeePercentage?: number | null;
	maxSupplyFeeAmount?: number | null;
	supplyFeeDateFrom?: string | null;
	supplyFeeDateTo?: string | null;
};

type CaseCreatedInput = string | Date | { toISODate?: () => string | null; toString: () => string };

export const defaultSumFeeResult: SumFeeResult = { sum: 0, fee: 0 };

export function shouldCalculateEnvironmentalFeeForDebitor(environmentFeePercentage: GetDebitor_debitor['environmentFeePercentage']): boolean {
	return environmentFeePercentage > 0;
}

export function hasMaximumEnvironmentalFeeAmountForDebitor(maxEnvironmentFeeAmount: GetDebitor_debitor['maxEnvironmentFeeAmount']): boolean {
	return maxEnvironmentFeeAmount > 0;
}

export const shouldCalculateEnvFeeForCategory = (category: string | undefined): boolean => {
	return category === 'FINAL' || category === 'SUB' || category === 'SCREENING';
};

export const shouldCalculateSupplyFeeForCategory = (category: string | undefined): boolean => shouldCalculateEnvFeeForCategory(category);

export function shouldCalculateSupplyFeeForDebitor(supplyFeePercentage: number | null | undefined): boolean {
	return (supplyFeePercentage ?? 0) > 0;
}

function toDateOnlyString(caseCreated: CaseCreatedInput): string | null {
	if (typeof caseCreated === 'string') {
		return caseCreated.split('T')[0] ?? null;
	}

	if (caseCreated instanceof Date) {
		return caseCreated.toISOString().split('T')[0] ?? null;
	}

	if (typeof caseCreated.toISODate === 'function') {
		return caseCreated.toISODate() ?? null;
	}

	return caseCreated.toString().split('T')[0] ?? null;
}

export function isCaseCreatedWithinSupplyFeeDateRange(caseCreated: CaseCreatedInput, debitor: Pick<SupplyFeeDebitor, 'supplyFeeDateFrom' | 'supplyFeeDateTo'>): boolean {
	const caseDate = toDateOnlyString(caseCreated);
	const supplyFeeDateFrom = debitor.supplyFeeDateFrom;
	const supplyFeeDateTo = debitor.supplyFeeDateTo;

	if (!caseDate || !supplyFeeDateFrom) {
		return false;
	}

	if (supplyFeeDateFrom === EMPTY_SUPPLY_FEE_DATE) {
		return false;
	}

	if (!supplyFeeDateTo || supplyFeeDateTo === EMPTY_SUPPLY_FEE_DATE) {
		return caseDate >= supplyFeeDateFrom;
	}

	return caseDate >= supplyFeeDateFrom && caseDate <= supplyFeeDateTo;
}

export function hasMaximumSupplyFeeAmountForDebitor(maxSupplyFeeAmount: number | null | undefined): boolean {
	return (maxSupplyFeeAmount ?? 0) > 0;
}

export function calculateEnvironmentalFeeForLines(
	lines:
		| {
			title?: string | null;
			price: number;
			value: number;
			excludeFromEnvFee: boolean | null;
		}[]
		| undefined,
	debitor: GetDebitor_debitor,
): SumFeeResult {
	if (!lines) {
		return { ...defaultSumFeeResult };
	}

	return lines.reduce(
		(sumFee, line) => {
			// Previously computed fee lines must never feed back into their own basis, or the fee compounds on every save.
			if (line.title === DEFAULT_ENVFEE_NAME || line.title === DEFAULT_SUPPLYFEE_NAME) {
				return sumFee;
			}

			const value = line.value === null ? 0 : isNaN(line.value) ? 0 : line.value;
			const result = (isNaN(line.price) ? 0 : line.price) * value;
			sumFee.sum += result;

			if (!line.excludeFromEnvFee) {
				sumFee.fee = calculateEnvironmentFee(result, sumFee.fee, debitor);
			}

			return sumFee;
		},
		{ ...defaultSumFeeResult },
	);
}

export function calculateSupplyFeeForLines(
	lines:
		| {
			title?: string | null;
			price: number;
			value: number;
			excludeFromSupplyFee: boolean | null;
		}[]
		| undefined,
	debitor: SupplyFeeDebitor,
): SumFeeResult {
	if (!lines) {
		return { ...defaultSumFeeResult };
	}

	return lines.reduce(
		(sumFee, line) => {
			// Previously computed fee lines must never feed back into their own basis, or the fee compounds on every save.
			if (line.title === DEFAULT_ENVFEE_NAME || line.title === DEFAULT_SUPPLYFEE_NAME) {
				return sumFee;
			}

			const value = line.value === null ? 0 : isNaN(line.value) ? 0 : line.value;
			const result = (isNaN(line.price) ? 0 : line.price) * value;
			sumFee.sum += result;

			if (!line.excludeFromSupplyFee) {
				sumFee.fee = calculateSupplyFee(result, sumFee.fee, debitor);
			}

			return sumFee;
		},
		{ ...defaultSumFeeResult },
	);
}

export type ScreeningTotalsLine = {
	title?: string | null;
	price: number;
	value: number;
	excludeFromEnvFee: boolean | null;
	excludeFromSupplyFee: boolean | null;
};

export interface ScreeningTotals {
	perScreeningSubtotal: number[];
	envFee: number;
	supplyFee: number;
	grandTotal: number;
}

// Single source of truth for screening subtotal/fee/grand-total math, shared by ScreeningBox and ScreeningModalTwo.
export function getScreeningTotals(
	screeningsMappedLines: ScreeningTotalsLine[][],
	debitor: GetDebitor_debitor,
	debitorWithSupply: SupplyFeeDebitor,
	options: { includeEnvFee: boolean; includeSupplyFee: boolean },
): ScreeningTotals {
	const perScreeningSubtotal = screeningsMappedLines.map(lines =>
		lines.reduce((sum, line) => {
			if (line.title === DEFAULT_ENVFEE_NAME || line.title === DEFAULT_SUPPLYFEE_NAME) {
				return sum;
			}

			const value = line.value === null ? 0 : isNaN(line.value) ? 0 : line.value;
			return sum + (isNaN(line.price) ? 0 : line.price) * value;
		}, 0),
	);

	const allLines = screeningsMappedLines.flat();
	const envFee = options.includeEnvFee ? calculateEnvironmentalFeeForLines(allLines, debitor).fee : 0;
	const supplyFee = options.includeSupplyFee ? calculateSupplyFeeForLines(allLines, debitorWithSupply).fee : 0;
	const grandTotal = perScreeningSubtotal.reduce((sum, subtotal) => sum + subtotal, 0) + envFee + supplyFee;

	return { perScreeningSubtotal, envFee, supplyFee, grandTotal };
}

function calculateEnvironmentFee(price: number, previousFee: number, debitor: GetDebitor_debitor): number {
	const currentFee = calculatePercentage(price, debitor);
	const enforceMaxEnvFeeAmount = hasMaximumEnvironmentalFeeAmountForDebitor(debitor.maxEnvironmentFeeAmount);
	const newFee = previousFee + currentFee;

	if (enforceMaxEnvFeeAmount && newFee > debitor.maxEnvironmentFeeAmount) {
		return debitor.maxEnvironmentFeeAmount;
	}

	return roundToTwoDecimalPlaces(newFee);
}

function calculateSupplyFee(price: number, previousFee: number, debitor: SupplyFeeDebitor): number {
	const currentFee = calculateSupplyPercentage(price, debitor);
	const enforceMaxSupplyFeeAmount = hasMaximumSupplyFeeAmountForDebitor(debitor.maxSupplyFeeAmount);
	const newFee = previousFee + currentFee;

	if (enforceMaxSupplyFeeAmount && newFee > (debitor.maxSupplyFeeAmount ?? 0)) {
		return debitor.maxSupplyFeeAmount ?? 0;
	}

	return roundToTwoDecimalPlaces(newFee);
}

export function calculateEnvironmentFeeScreening(price: number, debitor: GetDebitor_debitor): number {
	const currentFee = calculatePercentage(price, debitor);
	const enforceMaxEnvFeeAmount = hasMaximumEnvironmentalFeeAmountForDebitor(debitor.maxEnvironmentFeeAmount);
	if (enforceMaxEnvFeeAmount && currentFee > debitor.maxEnvironmentFeeAmount) {
		return debitor.maxEnvironmentFeeAmount;
	}
	return roundToTwoDecimalPlaces(currentFee);
}

function calculateEnvironmentalFeeForEditableInvoiceLine(line: EditableInvoiceLine, debitor: GetDebitor_debitor): number {
	if (line.excludeInEnvFee) return 0;
	const unitPrice = line.newUnitPrice ?? line.unitPrice ?? 0;
	const quantity = line.quantity ?? 0;
	return roundToTwoDecimalPlaces(calculatePercentage(unitPrice * quantity, debitor));
}

function calculatePercentage(num: number, debitor: GetDebitor_debitor) {
	return (num * debitor.environmentFeePercentage) / 100;
}

function roundToTwoDecimalPlaces(num: number): number {
	return Math.round((num + Number.EPSILON) * 100) / 100;
}

function calculateSupplyPercentage(num: number, debitor: SupplyFeeDebitor) {
	return (num * (debitor.supplyFeePercentage ?? 0)) / 100;
}

function isExcludedFromSupplyFeeBasis(line: EditableInvoiceLine): boolean {
	if (line.excludeInSupplyFee) {
		return true;
	}

	return SUPPLY_FEE_EXCLUDED_LINE_CODES.has((line.no ?? '').toUpperCase());
}

function isApplicableInvoiceForCaseSupplyFee(invoice: Pick<GetInvoices_invoices, 'category' | 'status'>): boolean {
	return SUPPLY_FEE_APPLICABLE_CATEGORIES.has(invoice.category) && !SUPPLY_FEE_IGNORED_STATUSES.has(invoice.status);
}

function applySupplyFeeCap(fee: number, debitor: SupplyFeeDebitor, accumulatedCaseSupplyFee: number): number {
	if (!hasMaximumSupplyFeeAmountForDebitor(debitor.maxSupplyFeeAmount)) {
		return roundToTwoDecimalPlaces(fee);
	}

	const maxSupplyFeeAmount = debitor.maxSupplyFeeAmount ?? 0;
	const remaining = maxSupplyFeeAmount - accumulatedCaseSupplyFee;
	if (remaining <= 0) {
		return 0;
	}

	return roundToTwoDecimalPlaces(Math.min(fee, remaining));
}

export function calculateTotalEnvFee(lines: EditableInvoiceLine[], shouldCalculateEnvFeeForDebitor: boolean, debitor: GetDebitor_debitor, category: string | undefined): number {
	if (!shouldCalculateEnvFeeForDebitor) {
		return 0;
	}
	const enforceMaxEnvFeeAmount = hasMaximumEnvironmentalFeeAmountForDebitor(debitor.maxEnvironmentFeeAmount);

	if (!category) {
		return 0;
	}

	// Calculate from actual invoice lines for all categories (SCREENING, FINAL, SUB)
	// Accumulate basis first, then apply percentage, then round - matching supply fee logic
	const basis = lines
		.filter(l => l.no !== DEFAULT_ENVFEE_NAME && l.no !== DEFAULT_SUPPLYFEE_NAME && !l.excludeInEnvFee)
		.reduce((sum, line) => {
			const quantity = line.quantity ?? 0;
			const unitPrice = line.newUnitPrice ?? line.unitPrice ?? 0;
			return sum + quantity * unitPrice;
		}, 0);

	if (basis <= 0) {
		return 0;
	}

	const envFee = roundToTwoDecimalPlaces(calculatePercentage(basis, debitor));
	return enforceMaxEnvFeeAmount && envFee > debitor.maxEnvironmentFeeAmount ? debitor.maxEnvironmentFeeAmount : envFee;
}

export function getEnvFeeOnInvoice(
	lines: { no: string; quantity: number | null; unitPrice: number | null; newUnitPrice: number | null; lineAmount?: number | null; excludeInEnvFee?: boolean | null }[],
	debitor: { environmentFeePercentage: number; maxEnvironmentFeeAmount: number },
): number {
	const enforceMaxEnvFeeAmount = hasMaximumEnvironmentalFeeAmountForDebitor(debitor.maxEnvironmentFeeAmount);

	// Calculate from actual invoice lines (exclude fee lines)
	// Prefer lineAmount when available (reliable for all invoice types including SCREENING)
	let envFee = 0;
	lines
		.filter(l => l.no !== DEFAULT_ENVFEE_NAME && l.no !== DEFAULT_SUPPLYFEE_NAME && !l.excludeInEnvFee)
		.forEach(line => {
			const amount = line.lineAmount != null
				? line.lineAmount
				: (line.quantity ?? 0) * (line.newUnitPrice ?? line.unitPrice ?? 0);
			envFee += (amount * debitor.environmentFeePercentage) / 100;
		});

	envFee = roundToTwoDecimalPlaces(envFee);
	return enforceMaxEnvFeeAmount && envFee > debitor.maxEnvironmentFeeAmount ? debitor.maxEnvironmentFeeAmount : envFee;
}

export function calculateTotalSupplyFee(
	lines: EditableInvoiceLine[],
	shouldCalculateSupplyFee: boolean,
	debitor: SupplyFeeDebitor,
	category: string | undefined,
	accumulatedCaseSupplyFee = 0,
): number {
	if (!shouldCalculateSupplyFee || !shouldCalculateSupplyFeeForCategory(category)) {
		return 0;
	}

	// Calculate from actual invoice lines for all categories (SCREENING, FINAL, SUB)
	const basis = lines.filter(line => !isExcludedFromSupplyFeeBasis(line)).reduce((sum, line) => {
		const quantity = line.quantity ?? 0;
		const unitPrice = line.newUnitPrice ?? line.unitPrice ?? 0;
		return sum + quantity * unitPrice;
	}, 0);

	if (basis <= 0) {
		return 0;
	}

	const fee = calculateSupplyPercentage(basis, debitor);
	return applySupplyFeeCap(fee, debitor, accumulatedCaseSupplyFee);
}

export function getSupplyFeeOnInvoice(
	lines: { no: string; quantity: number | null; unitPrice: number | null; newUnitPrice: number | null; lineAmount?: number | null; excludeInSupplyFee?: boolean | null }[],
	debitor: SupplyFeeDebitor,
	accumulatedCaseSupplyFee = 0,
): number {
	// Calculate from actual invoice lines (exclude fee lines and excluded line codes)
	// Prefer lineAmount when available (reliable for all invoice types including SCREENING)
	const basis = lines
		.filter(line => !line.excludeInSupplyFee && !SUPPLY_FEE_EXCLUDED_LINE_CODES.has((line.no ?? '').toUpperCase()))
		.reduce((sum, line) => {
			const amount = line.lineAmount != null
				? line.lineAmount
				: (line.quantity ?? 0) * (line.newUnitPrice ?? line.unitPrice ?? 0);
			return sum + amount;
		}, 0);

	if (basis <= 0) {
		return 0;
	}

	const fee = calculateSupplyPercentage(basis, debitor);
	return applySupplyFeeCap(fee, debitor, accumulatedCaseSupplyFee);
}

export function calculateAccumulatedCaseSupplyFee(
	invoices: GetInvoices_invoices[] | undefined,
	debitor: SupplyFeeDebitor,
	currentInvoiceReferenceNo?: string,
): number {
	if (!invoices || invoices.length === 0) {
		return 0;
	}

	let accumulated = 0;
	for (const invoice of invoices) {
		if (invoice.erpReferenceNo === currentInvoiceReferenceNo) {
			continue;
		}

		if (!isApplicableInvoiceForCaseSupplyFee(invoice)) {
			continue;
		}

		const invoiceSupplyFee = invoice.lines
			.filter(line => line.no === DEFAULT_SUPPLYFEE_NAME)
			.reduce((sum, line) => sum + (line.unitPrice ?? line.newUnitPrice ?? 0), 0);

		accumulated = applySupplyFeeCap(accumulated + invoiceSupplyFee, debitor, 0);
	}

	return accumulated;
}

